'use client'

import { Button } from '@brew/ui/button'
import { cn } from '@brew/ui/cn'
import { Input } from '@brew/ui/input'
import { useCallback, useState } from 'react'
import {
  ROUNDED_SEARCH_INPUT_CLASSNAME,
  RoundedSearchBar,
} from '@/components/audience/rounded-search-bar'
import { WordRotate } from '@/components/magicui/word-rotate'
import { MARKETING_CANVAS_BG } from '@/components/marketing/v2/marketing-layout'
import { useFlowSearchQuery } from '@/lib/flow-templates/search-query-state'
import { useTemplatesSurface } from '../lib/templates-surface-context'

const PLACEHOLDER_PREFIX = 'Search flows by brand:'
const ROTATING_WORDS = [
  'Notion…',
  'Atlassian…',
  'Substack…',
  'Ramp…',
  'a brand you admire…',
]

/**
 * The flows gallery's search box: an instant match on the brand over the
 * flows the page already holds. Every keystroke is the query — the grid
 * filters as you type through the shared `q` state — and the URL follows
 * after a short debounce, so a search survives a reload and a share.
 * Enter has nothing left to do; Clear empties the query.
 */
export function FlowsSearchBar() {
  const isPublicSurface = useTemplatesSurface() === 'public'
  const [query, setQuery] = useFlowSearchQuery()
  const [isFocused, setIsFocused] = useState(false)

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault()
    },
    []
  )
  const handleClear = useCallback(() => {
    void setQuery('')
  }, [setQuery])

  const shouldShowPlaceholder = !isFocused && !query

  return (
    <RoundedSearchBar
      onSubmit={handleSubmit}
      className={cn('h-11', { [MARKETING_CANVAS_BG]: isPublicSurface })}
    >
      <Input
        type="search"
        value={query}
        placeholder=""
        aria-label="Search flows by brand"
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onChange={(event) => void setQuery(event.currentTarget.value)}
        className={cn(ROUNDED_SEARCH_INPUT_CLASSNAME, 'h-full text-sm', {
          'bg-transparent': isPublicSurface,
        })}
      />
      {shouldShowPlaceholder ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute top-1 left-4 mt-2 flex select-none items-center gap-1"
        >
          <p className="font-medium text-muted-foreground text-sm">
            {PLACEHOLDER_PREFIX}
          </p>
          <WordRotate
            words={[...ROTATING_WORDS]}
            duration={4000}
            className="font-medium text-muted-foreground text-sm"
          />
        </div>
      ) : null}
      {query ? (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label="Clear search"
          className="shrink-0 rounded-full text-muted-foreground"
          onClick={handleClear}
        >
          Clear
        </Button>
      ) : null}
    </RoundedSearchBar>
  )
}
